// Import Redux toolkit
import { createSlice } from "@reduxjs/toolkit";

// Import Redux actions
import authConfig from "src/configs/auth";
import {
  fetchUser,
  login,
  register,
  forgotPassword,
  verifyOtp,
  resendOtp,
  resetPassword,
  fetchBusinessSubscription
} from "../actions/AuthAction";

const AuthSlice = createSlice({
  name: "Auth",
  initialState: {
    user: null,
    loading: false,
    error: null,
    isInitialized: false,
    loadingBtnLoader: false,
    forgotPasswordEmail: null,
    resetPasswordToken: null,
    isShowSetupMenu: false,
    /** When opening setup from dashboard (e.g. MenuTab), skip picker and open MenuSetup with this type */
    setupMenuDirectLaunch: null,
    profileType: localStorage.getItem("profileType") || "personal",
    selectedBusinessId: localStorage.getItem("selectedBusinessId") || null, 
    primaryLocationId: localStorage.getItem("primaryLocationId") || null,
    showLoader: false,
    showCompleteProfileDialog: false, // Control complete profile dialog
    showSubscriptionPlanDialog: {
      show: false,
      businessId: null,
      currentPlan: null,
    },
    showPaymentSuccessDialog: false,
    showPaymentErrorDialog: false,
    paymentErrorMessage: null,
    lastSubscriptionBusinessId: null, // Store businessId for retry
    isNewlyRegisteredBusiness: false, // Flag for newly registered business requiring subscription
    newBusinessId: null, // Store newly created business ID
    showBusinessDeactivationDialog: {
      show: false,
      businessId: null,
    },
    showDeleteAccountCancelDialog: false,
    businessSubscriptions: {}, // store fetched subscription info by businessId
    loadingSubscriptions: false,
    // subscriptionPlanBusinessId: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.error = null;
      state.forgotPasswordEmail = null;
      state.resetPasswordToken = null; // Clear reset password token
      state.isShowSetupMenu = false; // Clear reset password token
      state.setupMenuDirectLaunch = null;
      state.profileType = "personal";
      state.selectedBusinessId = null;
      state.primaryLocationId = null;
      state.showCompleteProfileDialog = false; // Hide dialog on logout
      window.localStorage.removeItem(authConfig.storageTokenKeyName);
      window.localStorage.removeItem(authConfig.storageUserDataKeyName);
      window.localStorage.removeItem(authConfig.storageUserIdKeyName);
      window.localStorage.removeItem("profileType");
      window.localStorage.removeItem("selectedBusinessId");
      window.localStorage.removeItem("primaryLocationId");
    },
    setShowLoader: (state, action) => {
      state.showLoader = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setForgotPasswordEmail: (state, action) => {
      state.forgotPasswordEmail = action.payload;
    },
    clearForgotPasswordEmail: (state) => {
      state.forgotPasswordEmail = null;
    },
    setResetPasswordToken: (state, action) => {
      state.resetPasswordToken = action.payload;
    },
    clearResetPasswordToken: (state) => {
      state.resetPasswordToken = null;
    },
    setIsShowSetupMenu: (state, action) => {
      state.isShowSetupMenu = action.payload;
    },
    setSetupMenuDirectLaunch: (state, action) => {
      state.setupMenuDirectLaunch = action.payload;
    },
    clearSetupMenuDirectLaunch: (state) => {
      state.setupMenuDirectLaunch = null;
    },
    setShowCompleteProfileDialog: (state, action) => {
      state.showCompleteProfileDialog = action.payload;
    },
    setShowSubscriptionPlanDialog: (state, action) => {
      state.showSubscriptionPlanDialog = action.payload;
    },
    setShowPaymentSuccessDialog: (state, action) => {
      state.showPaymentSuccessDialog = action.payload;
    },
    setShowPaymentErrorDialog: (state, action) => {
      state.showPaymentErrorDialog = action.payload;
    },
    setPaymentErrorMessage: (state, action) => {
      state.paymentErrorMessage = action.payload;
    },
    setLastSubscriptionBusinessId: (state, action) => {
      state.lastSubscriptionBusinessId = action.payload;
    },
    setNewlyRegisteredBusiness: (state, action) => {
      state.isNewlyRegisteredBusiness = action.payload.isNew;
      state.newBusinessId = action.payload.businessId;
    },
    clearNewlyRegisteredBusiness: (state) => {
      state.isNewlyRegisteredBusiness = false;
      state.newBusinessId = null;
    },
    setShowBusinessDeactivationDialog: (state, action) => {
      state.showBusinessDeactivationDialog = action.payload;
    },
    setShowDeleteAccountCancelDialog: (state, action) => {
      state.showDeleteAccountCancelDialog = action.payload;
    },
    switchProfile: (state, action) => {
      const { type, businessId, primaryLocationId } = action.payload;
      
      // Prevent unnecessary switches - if already on the target profile, don't update
      if (state.profileType === type && 
          state.selectedBusinessId === businessId && 
          state.primaryLocationId === primaryLocationId) {
        return;
      }
      
      state.profileType = type;
      state.selectedBusinessId = businessId;
      state.primaryLocationId = primaryLocationId;
      state.setupMenuDirectLaunch = null;
      localStorage.setItem("profileType", type);
      localStorage.setItem("selectedBusinessId", businessId || '');
      localStorage.setItem("primaryLocationId", primaryLocationId || '');
    },
    // Add new business to user's business list manually
    addBusinessToList: (state, action) => {
      if (state.user && action.payload) {
        if (!state.user.business) {
          state.user.business = [];
        }
        // Check if business already exists to avoid duplicates
        const businessExists = state.user.business.find(b => b._id === action.payload._id);
        if (!businessExists) {
          state.user.business.push(action.payload);
        }
      }
    },
    // Update user data in Redux store
    updateUser: (state, action) => {
      if (action.payload) {
        state.user = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login Cases
      .addCase(login.pending, (state) => {
        state.error = null;
        state.loadingBtnLoader = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loadingBtnLoader = false;
        if (action.payload.isDeleted === 1) {
          // Don't store user or "log in" - only show contact admin dialog
          state.user = null;
          state.showDeleteAccountCancelDialog = true;
        } else {
          state.user = action.payload.user;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload;
        state.loadingBtnLoader = false;
      })

      // Fetch User Cases
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isInitialized = true;
        
        // Automatically show complete profile dialog if profile is incomplete
        if (action.payload && action.payload.isProfileComplete === 0) {
          state.showCompleteProfileDialog = true;
        }
        
        // Show delete account cancel dialog if user is deleted
        if (action.payload && action.payload.isDeleted === 1) {
          state.showDeleteAccountCancelDialog = true;
        }
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isInitialized = false;
      })

      // Register cases
      .addCase(register.pending, (state) => {
        state.loadingBtnLoader = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = null;
        
        // Handle auto-login success after registration
        // If the registration action   auto-logged in the user,
        // we can immediately set the user data and mark the app as initialized
        // This eliminates the need for the user to manually login after registration
        if (action.payload.autoLoginSuccess && action.payload.user) {
          // Auto-login was successful - set the user data immediately
          state.user = action.payload.user;
          state.isInitialized = true;
          
          // Note: The user is now fully authenticated and can access protected routes
          // No additional login step is required
        }
        // If auto-login failed, the user will need to manually login
        // The registration was still successful, but user data is not set
      })
      .addCase(register.rejected, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = action.payload;
      })

      // Forgot Password cases
      .addCase(forgotPassword.pending, (state) => {
        state.loadingBtnLoader = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loadingBtnLoader = false;
        state.forgotPasswordEmail = action.payload.email; // Store email
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = action.payload;
      })

      // Verify OTP cases
      .addCase(verifyOtp.pending, (state) => {
        state.loadingBtnLoader = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = null;
        // Store the reset password token from the response
        if (action.payload.token) {
          state.resetPasswordToken = action.payload.token;
        }
        // Keep email stored for reset password step
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = action.payload;
      })

      // Resend OTP cases
      .addCase(resendOtp.pending, (state) => {
        state.loadingBtnLoader = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = null;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = action.payload;
      })

      // Reset-Password cases
      .addCase(resetPassword.pending, (state) => {
        state.loadingBtnLoader = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loadingBtnLoader = false;
        state.error = null;
        state.forgotPasswordEmail = null; // Clear email after successful reset
        state.resetPasswordToken = null; // Clear reset password token after successful reset
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loadingBtnLoader = false;
        state.error = action.payload;
      })

      // Fetch Business Subscription cases
      .addCase(fetchBusinessSubscription.pending, (state) => {
        state.loadingSubscriptions = true;
      })
      .addCase(fetchBusinessSubscription.fulfilled, (state, action) => {
        state.loadingSubscriptions = false;
        if (action.payload?.businessId) {
          state.businessSubscriptions[action.payload.businessId] = action.payload.data;
        }
      })
      .addCase(fetchBusinessSubscription.rejected, (state) => {
        state.loadingSubscriptions = false;
      });
  },
});

export const {
  logout,
  clearError,
  setForgotPasswordEmail,
  clearForgotPasswordEmail,
  setResetPasswordToken,
  clearResetPasswordToken,
  setIsShowSetupMenu,
  setSetupMenuDirectLaunch,
  clearSetupMenuDirectLaunch,
  setShowCompleteProfileDialog,
  switchProfile,
  setShowLoader,
  addBusinessToList,
  updateUser,
  setShowSubscriptionPlanDialog,
  setShowPaymentSuccessDialog,
  setShowPaymentErrorDialog,
  setPaymentErrorMessage,
  setLastSubscriptionBusinessId,
  setNewlyRegisteredBusiness,
  clearNewlyRegisteredBusiness,
  setShowBusinessDeactivationDialog,
  setShowDeleteAccountCancelDialog,
} = AuthSlice.actions;

export default AuthSlice.reducer;